import { i18n } from "../../i18n/i18n";

import React, { Component, Fragment } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { find, includes } from 'lodash'
import { produce } from 'immer'
import {
  replaceCloning,
  Markup as LabelBuilderMarkup,
  Type as LabelType
} from '../../views/LabelBuilder/LabelBuilder.slice'
import contactApi, { ContactData } from '../../apis/contact.api'
import customLabelApi, { CustomLabelData } from '../../apis/customLabel.api'
import itemApi, { ItemData } from '../../apis/item.api'
import labelApi from '../../apis/label.api'
import customLabelService from '../../services/customLabel.service'
import confirmService from '../../services/confirm.service'
import alertService from '../../services/alert.service'
import contactService from '../../services/contact.service'
import itemService from '../../services/item.service'
import { ConfirmParams } from '../Confirm/Confirm.state'
import { RootState } from '../../store'
import Heading from '../Heading'
import SearchPicker, { SearchPickerOption } from '../SearchPicker'
import Select from '../Select'
import Button from '../Button'
import categoryApi, { CategoryData } from "../../apis/category.api";
import categoryService from "../../services/category.service";
import {useUserPlan} from "../../utils/userPlan.util";

interface Props {}

interface State {
  target: LabelBuilderMarkup['target']
  type: LabelType | null
  contactId: number | null
  itemId: number | null
  customLabelId: number | null
  contactName: string
  item: string
  customLabel: string
  contact: ContactData | null
  contacts: ContactData[]
  items: ItemData[]
  customLabels: CustomLabelData[]
  categories: CategoryData[]
  category: string
  categoryId: number | null
}

type CombinedProps = Props & ConnectedProps<typeof connector>

class CloneLabelProperties extends Component<CombinedProps, State> {
  constructor (props: CombinedProps) {
    super(props)
    const userPlan = useUserPlan();

    this.state = {
      target: userPlan.isLabelBuilderUser ? 'customLabel' : 'site',
      type: null,
      contactId: null,
      itemId: null,
      customLabelId: null,
      contactName: '',
      item: '',
      customLabel: '',
      contact: null,
      contacts: [],
      items: [],
      customLabels: [],
      categories: [],
      category: '',
      categoryId: null
    }

    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleSearch = this.handleSearch.bind(this)
    this.handleSearchSelect = this.handleSearchSelect.bind(this)
    this.handleSearchPickerBlur = this.handleSearchPickerBlur.bind(this)
  }

  handleInputChange (name: string, value: string) {
    if (name === 'target') {
      const target = value as LabelBuilderMarkup['target']
      this.setState({ target })
    } else if (name === 'type') {
      const type = value as LabelBuilderMarkup['type']
      this.setState({ type })
    } else if (name === 'contact') {
      this.setState({
        type: null,
        contactId: null,
        contact: null,
        contactName: value
      })
    } else if (name === 'item') {
      this.setState({
        itemId: null,
        type: null,
        item: value
      })
    } else if (name === 'category') {
      this.setState({
        categoryId: null,
        type: null,
        category: value
      })
    } else if (name === 'customLabel') {
      this.setState({
        customLabelId: null,
        type: null,
        customLabel: value
      })
    }
  }

  handleSearch (name: string, term: string) {
    if (name === 'contact') {
      contactApi.search(term, contacts => {
        if (!contacts) return
        this.setState({ contacts })
      })
    } else if (name === 'item') {
      itemApi.search(term, items => {
        if (!items) return
        this.setState({ items })
      })
    } else if (name === 'category') {
        if (term.length === 0) return
        categoryApi.search(term, categories => {
          if (!categories) return
          this.setState({ categories })
        })
      } else if (name === 'customLabel') {
      customLabelApi.search(term, customLabels => {
        if (!customLabels) return
        this.setState({ customLabels })
      })
    }
  }

  handleSearchSelect (name: string, option: SearchPickerOption) {
    if (name === 'contact') {
      const id = option.id as number
      const contact = find(this.state.contacts, { id })
      this.setState({ contacts: [], contact: contact || null, contactId: id })
    } else if (name === 'item') {
      const id = option.id as number
      this.setState({ items: [], itemId: id })
    } else if (name === 'customLabel') {
      const id = option.id as number
      this.setState({ customLabels: [], customLabelId: id })
    } else if (name === 'category') {
      const id = option.id as number
      this.setState({ categories: [], categoryId: id })
    } 
  }

  handleSearchPickerBlur (name: string) {
    if (name === 'contact') {
      this.setState({ contacts: [] })
    } else if (name === 'item') {
      this.setState({ items: [] })
    } else if (name === 'customLabel') {
      this.setState({ customLabels: [] })
    } else if (name === 'category') {
      this.setState({ categories: [] })
    }
  }

  handleCancelClick = () => {
    this.props.replaceCloning(false)
  }

  handleCloneConfirm = () => {
    const { id } = this.props   // <-- existing label id

    const { target, type, contactId, itemId, customLabelId } = this.state
    const markup = produce(this.props.markup, draft => {
      draft.target = target
      draft.type = type
      draft.contactId = contactId
      draft.itemId = itemId
      draft.customLabelId = customLabelId
    })
    const payload = {
      existingLabelId: id,
      markup
    }
    labelApi.clone(payload, label => {
      if (!label) return
      alertService.success('Label has been successfully cloned')
      this.props.replaceCloning(false)
    })
  }

  handleCloneClick = () => {
    const confirm: ConfirmParams = {
      id: 0,
      type: 'warning',
      question: i18n('Clone label will replace the target label with the source label. Are you sure to continue?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.handleCloneConfirm()
    }
    confirmService.push(confirm)
  }

  get targetOptions () {
    return [
      { name: 'Global', value: 'site' },
      { name: 'Contact', value: 'contact' },
      { name: 'Item', value: 'item' },
      { name: 'Custom label', value: 'customLabel' },
      { name: 'Category', value: 'category' }
    ]
  }

  userPlan = useUserPlan();

  targetLabelOptions = this.userPlan.isLabelBuilderUser
      ? this.targetOptions.filter(option => option.name === 'Custom label')
      : this.targetOptions

  get typeOptions () {
    const typeOptions = {
      default: [
        { name: 'Inventory pallet', value: 'inventoryPallet' },
        { name: 'Inventory case', value: 'inventoryCase' },
        { name: 'Receiving pallet', value: 'receivingPallet' },
        { name: 'Receiving case', value: 'receivingCase' }
      ],
      customer: [
        { name: 'Inventory pallet', value: 'inventoryPallet' },
        { name: 'Inventory case', value: 'inventoryCase' }
      ],
      vendor: [
        { name: 'Receiving pallet', value: 'receivingPallet' },
        { name: 'Receiving case', value: 'receivingCase' }
      ]
    }
    const { contact, target } = this.state
    if (target === 'contact' && contact) {
      const { contactableType } = contact
      return typeOptions[contactableType]
    } else {
      return typeOptions.default
    }
  }

  get contactSearchOptions () {
    return contactService.searchOptions(this.state.contacts)
  }

  get itemSearchOptions () {
    return itemService.searchOptions(this.state.items)
  }

  get categorySearchOptions () {
    return categoryService.searchOptions(this.state.categories)
  }

  get customLabelSearchOptions () {
    return customLabelService.searchOptions(this.state.customLabels)
  }

  get contactTargetForm () {
    const { target } = this.state
    return (target === 'contact') &&
      <Fragment>
        <SearchPicker
          name="contact"
          value={this.state.contactName}
          options={this.contactSearchOptions}
          label={i18n("Contact")}
          onSearch={this.handleSearch}
          onChange={this.handleInputChange}
          onSelect={this.handleSearchSelect}
          onBlur={this.handleSearchPickerBlur} />
      </Fragment>
  }

  get itemTargetForm () {
    const { target } = this.state
    return (target === 'item') &&
      <SearchPicker
        name="item"
        value={this.state.item}
        options={this.itemSearchOptions}
        label={i18n("Item")}
        onSearch={this.handleSearch}
        onChange={this.handleInputChange}
        onSelect={this.handleSearchSelect}
        onBlur={this.handleSearchPickerBlur} />
  }

    get categoryTargetForm () {
      const { target } = this.state
      return (target === 'category') &&
        <SearchPicker
          name="category"
          value={this.state.category}
          options={this.categorySearchOptions}
          label={i18n("Category")}
          onSearch={this.handleSearch}
          onChange={this.handleInputChange}
          onSelect={this.handleSearchSelect}
          onBlur={this.handleSearchPickerBlur} />
    }

  get customLabelTargetForm () {
    const { target } = this.state
    return (target === 'customLabel') &&
      <SearchPicker
        name="customLabel"
        value={this.state.customLabel}
        options={this.customLabelSearchOptions}
        label={i18n("Custom label")}
        onSearch={this.handleSearch}
        onChange={this.handleInputChange}
        onSelect={this.handleSearchSelect}
        onBlur={this.handleSearchPickerBlur} />
  }

  get labelType () {
    const { target } = this.state
    console.log("target ==== ",target)
    const availableTargets = ['site', 'contact', 'item', 'category']
    return (includes(availableTargets, target)) &&
      <Select
        name="type"
        value={this.state.type || ''}
        options={this.typeOptions}
        label={i18n("Label type")}
        onChange={this.handleInputChange} />
  }

  render () {
    return (
      <div>
        <Heading
          className="mb-2"
          text={i18n("Clone label")}
          size="small" />
        <Select
          name="target"
          value={this.state.target}
          options={this.targetLabelOptions}
          label={i18n("Label target")}
          onChange={this.handleInputChange} />
        {this.contactTargetForm}
        {this.itemTargetForm}
        {this.categoryTargetForm}
        {this.customLabelTargetForm}
        {this.labelType}
        <div className="flex justify-end gap-2">
          <Button
            type='secondary'
            text={i18n("Cancel")}
            onClick={this.handleCancelClick} />
          <Button
            text={i18n("Clone")}
            onClick={this.handleCloneClick} />
        </div>
      </div>
    )
  }
}

function mapStateTopProps (state: RootState, props: Props) {
  const { labelBuilder } = state
  const { markup, id } = labelBuilder

  return {
    markup,
    id
  }
}

const mapDispatchToProps = {
  replaceCloning
}

const connector = connect(mapStateTopProps, mapDispatchToProps)
export default connector(CloneLabelProperties)
